import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { uploadAttachmentWithProgress } from '@/components/chat/ChatComposer/uploadAttachmentWithProgress';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/fieldError';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Textarea } from '@/components/ui/textarea';
import {
  SUPPORT_MEDIA_ACCEPT_ATTRIBUTE,
  SUPPORT_MEDIA_ALLOWED_CONTENT_TYPES,
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_PRIORITIES,
} from '@/constants/support';
import { isAcceptedContentType } from '@/constants/media';
import { useCreateSupportTicketMutation, useInitSupportDraftAttachmentMutation } from '@/hooks/useSupport';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import {
  buildCategoryTranslationKey,
  buildPriorityTranslationKey,
} from '@/components/support/supportLabels';

interface NewTicketDialogProps {
  onClose: () => void;
  onCreated: (ticketId: string) => void;
}

interface DraftAttachment {
  file: File;
  attachmentId: string | null;
  status: 'uploading' | 'ready' | 'failed';
  progressPercent: number;
}

export function NewTicketDialog({ onClose, onCreated }: NewTicketDialogProps) {
  const { t } = useTranslation(['support', 'common']);
  const createMutation = useCreateSupportTicketMutation();
  const initDraftAttachmentMutation = useInitSupportDraftAttachmentMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<string>(SUPPORT_TICKET_CATEGORIES[0]);
  const [priority, setPriority] = useState('normal');
  const [body, setBody] = useState('');
  const [draftAttachment, setDraftAttachment] = useState<DraftAttachment | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const subjectError = showErrors === true && isBlank(subject) ? 'validation:requiredField' : null;
  const bodyError = showErrors === true && isBlank(body) ? 'validation:requiredField' : null;

  const categoryOptions = SUPPORT_TICKET_CATEGORIES.map(function toCategoryOption(value) {
    return { value, label: t(buildCategoryTranslationKey(value)) };
  });
  const priorityOptions = SUPPORT_TICKET_PRIORITIES.map(function toPriorityOption(value) {
    return { value, label: t(buildPriorityTranslationKey(value)) };
  });

  async function handleFileSelected(selectedFile: File) {
    if (isAcceptedContentType(selectedFile, SUPPORT_MEDIA_ALLOWED_CONTENT_TYPES) === false) {
      showErrorNotification(t('support:errors.uploadFailed'));
      return;
    }
    setDraftAttachment({ file: selectedFile, attachmentId: null, status: 'uploading', progressPercent: 0 });
    try {
      const initResult = await initDraftAttachmentMutation.mutateAsync({
        filename: selectedFile.name,
        contentType: selectedFile.type,
        sizeBytes: selectedFile.size,
      });
      await uploadAttachmentWithProgress(
        initResult.uploadUrl,
        selectedFile,
        initResult.contentType,
        function reportProgress(progressPercent) {
          setDraftAttachment(function applyProgress(current) {
            return current === null ? current : { ...current, progressPercent };
          });
        }
      );
      setDraftAttachment({
        file: selectedFile,
        attachmentId: initResult.attachmentId,
        status: 'ready',
        progressPercent: 100,
      });
    } catch {
      setDraftAttachment({ file: selectedFile, attachmentId: null, status: 'failed', progressPercent: 0 });
      showErrorNotification(t('support:errors.uploadFailed'));
    }
  }

  function submitTicket(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (isBlank(subject) || isBlank(body)) {
      setShowErrors(true);
      return;
    }
    const attachmentIds =
      draftAttachment !== null &&
      draftAttachment.status === 'ready' &&
      draftAttachment.attachmentId !== null
        ? [draftAttachment.attachmentId]
        : [];
    createMutation.mutate(
      { subject: subject.trim(), category, priority, body: body.trim(), attachmentIds },
      {
        onSuccess: function handleCreated(ticket) {
          showSuccessNotification(t('support:newTicket.success'));
          onCreated(ticket.id);
        },
        onError: function handleError(creationError) {
          const backendMessage = resolveApiErrorMessage(creationError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('support:errors.generic')
          );
        },
      }
    );
  }

  return (
    <ModalOverlay onRequestClose={onClose} ariaLabel={t('support:newTicket.title')}>
      <div className="w-full max-w-lg bg-zoowhite rounded-2xl border border-zooborder p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-zooindigo">{t('support:newTicket.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:actions.close')}
            className="text-zoomuted hover:text-zooindigo"
          >
            <Icon name="X" />
          </button>
        </div>
        <form onSubmit={submitTicket} className="space-y-4">
          <div>
            <Label htmlFor="support-subject">{t('support:newTicket.subject')}</Label>
            <Input
              id="support-subject"
              value={subject}
              hasError={subjectError !== null}
              onChange={function updateSubject(changeEvent) {
                setSubject(changeEvent.target.value);
              }}
            />
            <FieldError messageKey={subjectError} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('support:newTicket.category')}</Label>
              <SearchableSelect options={categoryOptions} value={category} onChange={setCategory} />
            </div>
            <div>
              <Label>{t('support:newTicket.priority')}</Label>
              <SearchableSelect options={priorityOptions} value={priority} onChange={setPriority} />
            </div>
          </div>
          <div>
            <Label htmlFor="support-body">{t('support:newTicket.body')}</Label>
            <Textarea
              id="support-body"
              value={body}
              onChange={function updateBody(changeEvent) {
                setBody(changeEvent.target.value);
              }}
            />
            <FieldError messageKey={bodyError} />
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept={SUPPORT_MEDIA_ACCEPT_ATTRIBUTE}
              className="hidden"
              onChange={function onFilePicked(changeEvent) {
                const selectedFile =
                  changeEvent.target.files === null ? null : changeEvent.target.files[0];
                if (selectedFile !== undefined && selectedFile !== null) {
                  handleFileSelected(selectedFile);
                }
              }}
            />
            {draftAttachment === null ? (
              <Button
                type="button"
                variant="outline"
                size="chip"
                onClick={function openFilePicker() {
                  if (fileInputRef.current !== null) {
                    fileInputRef.current.click();
                  }
                }}
              >
                <Icon name="Paperclip" />
                {t('support:newTicket.attach')}
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-zooindigo">
                <Icon name="Paperclip" className="shrink-0 text-zooviolet" />
                <span className="flex-1 truncate">{draftAttachment.file.name}</span>
                {draftAttachment.status === 'uploading' ? (
                  <span className="text-xs font-semibold text-zooviolet">
                    {draftAttachment.progressPercent}%
                  </span>
                ) : null}
                {draftAttachment.status === 'failed' ? (
                  <span className="text-zooerrortext font-semibold">
                    {t('support:errors.uploadFailed')}
                  </span>
                ) : null}
                <button
                  type="button"
                  aria-label={t('support:composer.removeAttachment')}
                  onClick={function removeAttachment() {
                    setDraftAttachment(null);
                    if (fileInputRef.current !== null) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-zoomuted hover:text-zooerrortext"
                >
                  <Icon name="X" />
                </button>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              {t('support:newTicket.submit')}
            </Button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}
